import type { FC } from 'react';

import AreaLineChart from '@/common-components/charts/AreaLineChart';
import Pill from '@/common-components/Pill';
import { X_AXIS_LABEL_COUNT } from '@/features/dashboard/constants';
import type { TimeseriesPoint } from '@/types/stats.types';
import { formatRupeesCompact } from '@/utils/currency.util';

interface RevenueGrowthCardProps {
  points: TimeseriesPoint[];
  revenueMtdPaise: number;
  days: number;
}

const LEGEND = [
  { label: 'Revenue', color: 'var(--color-brand-500)' },
  { label: 'Users', color: 'var(--color-info)' },
  { label: 'Rentals', color: 'var(--color-success)' },
];

/** Samples evenly across the series so the axis never crowds, however long the window is. */
const axisLabels = (points: TimeseriesPoint[]): string[] => {
  if (points.length === 0) return [];
  const step = Math.max(1, Math.floor((points.length - 1) / (X_AXIS_LABEL_COUNT - 1)));
  const labels: string[] = [];
  for (let i = 0; i < points.length; i += step) {
    const point = points[i];
    if (!point) continue;
    labels.push(
      new Date(point.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
    );
  }
  labels[labels.length - 1] = 'Today';
  return labels;
};

const RevenueGrowthCard: FC<RevenueGrowthCardProps> = ({ points, revenueMtdPaise, days }) => {
  const maxRevenue = Math.max(0, ...points.map((p) => p.revenuePaise));

  return (
    <section className="rounded-2xl border border-[var(--color-divider)] bg-white p-6 shadow-[var(--shadow-card)]">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-extrabold text-[var(--color-ink)]">Revenue &amp; Growth</h2>
          <p className="text-[12px] text-[var(--color-ink-muted)]">
            Platform earnings — last {days} days
          </p>
        </div>
        <Pill tone="brand">{formatRupeesCompact(revenueMtdPaise)} MTD</Pill>
      </header>

      <div className="mb-4 flex flex-wrap gap-4">
        {LEGEND.map((item) => (
          <div
            key={item.label}
            className="flex items-center gap-1.5 text-[11.5px] text-[var(--color-ink-muted)]"
          >
            <span className="h-2 w-2 rounded-full" style={{ background: item.color }} />
            {item.label}
          </div>
        ))}
      </div>

      {points.length === 0 ? (
        <p className="py-10 text-center text-[13px] text-[var(--color-ink-muted)]">
          No activity in this window yet.
        </p>
      ) : (
        <AreaLineChart
          series={[
            {
              id: 'revenue',
              label: 'Revenue',
              color: 'var(--color-brand-500)',
              values: points.map((p) => p.revenuePaise),
              area: true,
            },
            {
              id: 'users',
              label: 'Users',
              color: 'var(--color-info)',
              values: points.map((p) => p.newUsers),
              dashed: true,
            },
            {
              id: 'bookings',
              label: 'Rentals',
              color: 'var(--color-success)',
              values: points.map((p) => p.bookings),
            },
          ]}
          xLabels={axisLabels(points)}
          // The chart resolves what each grid line is worth and calls this for its label, so the
          // axis reads correctly whatever the data does.
          formatY={maxRevenue > 0 ? formatRupeesCompact : undefined}
        />
      )}
    </section>
  );
};

export default RevenueGrowthCard;
